import { ExclamationCircleOutlined } from "@ant-design/icons";
import { UpdateStageRequestAwsRegionEnum } from "@microtica/ms-engine-sdk";
import { Button, Card, Col, Divider, Form, Row, Select, Space, notification, Tag } from "antd";
import { RequiredMark } from "antd/lib/form/Form";
import { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router";
import { getCloudService, getEnvironmentService } from "../../backend";
import DeploymentStatusTag from "../../components/DeploymentStatusTag";
import ConnectAwsAccountModal from "../../components/settings/ConnectAwsAccountModal";
import { AWS_REGIONS } from "../../enums/enums";
import { AwsRegionType, IEnvDetails } from "../../types";
import AwsLogo from "../../assets/aws-logo.svg";
import { trackSpecCloudAccountInit } from "../../backend/tracking/user-settings";
import Title from "antd/lib/typography/Title";
import { SearchOutlined } from "@ant-design/icons";
import { useDoubleConfigurationModal } from "../../contexts/Modal";
import { handleUndeployEnvironment } from "../../utils/environment/handle-undeploy-environment";

export default function EnvironmentSettingsInfrastructure() {
    const [accountForm] = Form.useForm();
    const navigate = useNavigate();
    const { projectId, envId } = useParams();
    const { envDetails } = useOutletContext<{ envDetails: IEnvDetails }>();
    const [requiredMark] = useState<RequiredMark>('optional');
    const [awsAccounts, setAwsAccounts] = useState<{ id: string; name: string }[]>([]);
    const [loadingUpdateAccount, setLoadingUpdateAccount] = useState(false);
    const [connectAwsAccountModalVisible, setConnectAwsAccountModalVisible] = useState(false);
    const { open: openConfirmModal } = useDoubleConfigurationModal();

    const [canUndeploy] = useState(
        [
            "CREATE_COMPLETE",
            "UPDATE_COMPLETE",
            "ROLLBACK_COMPLETE",
            "UPDATE_ROLLBACK_COMPLETE",
            "DELETE_FAILED"
        ].includes(envDetails.status || "")
    );

    const [canDelete] = useState(
        ![
            "CREATE_COMPLETE",
            "UPDATE_COMPLETE",
            "ROLLBACK_COMPLETE",
            "UPDATE_ROLLBACK_COMPLETE",
            "CREATE_IN_PROGRESS",
            "DELETE_IN_PROGRESS",
            "ROLLBACK_IN_PROGRESS",
            "UPDATE_IN_PROGRESS",
            "UPDATE_COMPLETE_CLEANUP_IN_PROGRESS",
            "UPDATE_ROLLBACK_COMPLETE_CLEANUP_IN_PROGRESS",
            "UPDATE_ROLLBACK_IN_PROGRESS",
            "DELETE_FAILED"
        ].includes(envDetails.status || "")
    );

    const [canUpdate, setCanUpdate] = useState(canDelete);

    async function handleUpdateAccount(values: { accountId: string, region: AwsRegionType }) {
        try {
            setLoadingUpdateAccount(true);
            await getEnvironmentService().updateStage(
                envId!,
                projectId!,
                {
                    awsAccountId: values.accountId,
                    awsRegion: UpdateStageRequestAwsRegionEnum[values.region]
                }
            );
            notification.success({
                message: "AWS account updated",
                description: "The AWS account for this environment has been updated successfully",
            });
            setCanUpdate(false);
        } catch (error: any) {
            notification.error({
                message: "AWS account can not be updated",
                description: error?.response?.data ? error.response.data.message : "We encountered an error while updating this environment. Please contact our support team on support@microtica.com or write on our Discord community channel."
            });
        } finally {
            setLoadingUpdateAccount(false);
        }
    }

    useEffect(() => {
        loadData();
    }, []);

    async function loadData() {
        const { data: { awsAccounts } } = await getCloudService().getAwsAccounts(projectId!);

        setAwsAccounts(awsAccounts.map(acc => ({ id: acc.id, name: acc.accountName })));
        // enable the user to update the stage's AWS account if there is no account connected to the stage but there are accounts added to the project
        if (!envDetails.awsAccountId && !!awsAccounts.length) {
            setCanUpdate(true);
        }
    }

    return (
        <Space direction="vertical" className="full-width">
            <Card bordered>
                <Card.Meta
                    title="Cloud Account"
                    description="Environment resources will be deployed on the selected cloud account and region."
                />
                <Divider />
                {awsAccounts && awsAccounts.length ?
                    <Form
                        form={accountForm}
                        layout="vertical"
                        initialValues={{
                            accountId: envDetails.awsAccountId,
                            region: envDetails.region && AWS_REGIONS.find(r => r.value === envDetails.region)!.id
                        }}
                        requiredMark={requiredMark}
                        onFinish={handleUpdateAccount}
                    >
                        <Form.Item
                            name="accountId"
                            label="Cloud account"
                            required
                            rules={[{ required: true, message: 'Please select cloud account!' }]}
                        >
                            <Select
                                showSearch
                                style={{ width: "100%" }}
                                placeholder="Select account"
                                disabled={!canUpdate}
                                filterOption={(input, option: any) => {
                                    return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }}
                                filterSort={(optionA, optionB) => {
                                    return optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
                                }}
                            >
                                {
                                    awsAccounts.map(acc => (
                                        <Select.Option value={acc.id}>
                                            {acc.name}
                                        </Select.Option>
                                    ))
                                }
                            </Select>
                        </Form.Item>
                        <Form.Item
                            name="region"
                            label="Cloud region"
                            required
                            rules={[{ required: true, message: 'Please select cloud region!' }]}
                        >
                            <Select
                                showSearch
                                style={{ width: "100%" }}
                                placeholder="Select region"
                                disabled={!canUpdate}
                                filterOption={(input, option: any) => {
                                    return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }}
                                filterSort={(optionA, optionB) => {
                                    return optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
                                }}
                            >
                                {
                                    AWS_REGIONS.map(region => (
                                        <Select.Option value={region.id}>{region.name}</Select.Option>
                                    ))
                                }
                            </Select>
                        </Form.Item>
                        <Divider />
                        <Row style={{
                            display: "flex",
                            alignItems: "center"
                        }}>
                            <Col span={12}>
                                {
                                    canUpdate ? undefined :
                                        <>
                                            Account cannot be updated <DeploymentStatusTag status={envDetails.status} />
                                        </>
                                }
                            </Col>
                            <Col span={12} style={{ textAlign: "right" }}>
                                <Button htmlType="submit" disabled={!canUpdate} loading={loadingUpdateAccount}>
                                    Save
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                    :
                    <div style={{ textAlign: "center" }}>
                        <div style={{ textAlign: 'center' }}>
                            <SearchOutlined style={{ fontSize: 60, verticalAlign: "middle", color: "#244460" }} />
                            <Title level={5} style={{ color: "black" }}>It looks like you haven't connected any cloud accounts yet</Title>
                            <p style={{ color: "black" }}>Connect a cloud account to deploy your infrastructure</p>
                        </div>
                        <Button onClick={() => {
                            setConnectAwsAccountModalVisible(true);
                            trackSpecCloudAccountInit("aws");
                        }}>
                            <Space>
                                <img src={AwsLogo} style={{ width: "20px" }} alt="aws" /> Connect AWS Account
                            </Space>
                        </Button>
                    </div>
                }
                {
                    // Workaround. The modal is not unmouting when closed so we need to unmount the whole component using 'connectAwsAccountModalVisible'
                    // We have a process of periodic API calls which remain active even if when the modal closes
                    connectAwsAccountModalVisible &&
                    <ConnectAwsAccountModal
                        visible={connectAwsAccountModalVisible}
                        onOk={() => {
                            loadData();
                            setConnectAwsAccountModalVisible(false);
                        }}
                        onCancel={() => setConnectAwsAccountModalVisible(false)}
                    />
                }
            </Card>
            <br />
            <Card bordered>
                <Card.Meta
                    title="Replicate (Coming soon)"
                    description="Create a full clone of your infrastructure into a new environment."
                />
                <Divider />
                <Row style={{
                    display: "flex",
                    "alignItems": "center"
                }}>
                    <Col span={24} style={{ textAlign: "right" }}>
                        <Button
                            htmlType="submit"
                            disabled={true}
                        >
                            Replicate
                        </Button>
                    </Col>
                </Row>
            </Card>
            <br />
            <Card bordered>
                <Card.Meta
                    title={
                        <Space>
                            <ExclamationCircleOutlined style={{ color: 'red' }} />
                            Undeploy
                        </Space>
                    }
                    description="This action will remove all resources from your cloud account. However, the environment will still remain available here."
                />
                <Divider />
                <Row style={{
                    display: "flex",
                    "alignItems": "center"
                }}>
                    <Col span={12}>
                        {
                            !envDetails.awsAccountId ?
                                <>
                                    Undeploy is disabled <Tag>No connected AWS account</Tag>
                                </> :
                                !canUndeploy ?
                                    <>
                                        Undeploy is disabled <DeploymentStatusTag status={envDetails.status} />
                                    </> : null
                        }
                    </Col>
                    <Col span={12} style={{ textAlign: "right" }}>
                        <Button
                            type="primary"
                            danger
                            disabled={!canUndeploy}
                            onClick={() => openConfirmModal({
                                title: "Undeploy Infrastructure?",
                                description: "The infrastructure resources in the environment will be permanently deleted from your cloud account.",
                                confirmation: envId!,
                                alert: <b>This action cannot be undone.</b>,
                                cancelText: "Cancel",
                                okText: "Undeploy Infrastructure",
                                onOk: () => handleUndeployEnvironment({
                                    envId: envId!,
                                    projectId: projectId!,
                                    navigate
                                })
                            })}
                        >
                            Undeploy
                        </Button>
                    </Col>
                </Row>
            </Card>
        </Space>
    );

}